// tawkService.js
//
// Web and mobile now use two different Tawk integrations, both fed by the
// same hub-lookup helpers below:
//
// - Web: unchanged — Tawk's own embed <script> is injected and its floating
//   launcher is used as-is (loadTawk/destroyTawk/syncTawk), hub resolved
//   from the URL by App.js.
// - Mobile: no script injection at all. getTawkChatUrl() builds Tawk's
//   direct chat-page URL, which SupportChatModal loads in an <iframe> inside
//   an app-owned modal, opened from an app-owned button (Login's pre-auth
//   support button, BottomTabBar's Support tab post-auth). This was a
//   deliberate move away from embedding Tawk's script on mobile: Tawk's own
//   injected launcher iframe would render but sit unresponsive to taps after
//   the Cordova iOS login transition until the app was hard-reloaded — a
//   DOM/event-lifecycle quirk in Tawk's own bootstrap, not something this
//   app's code could reliably detect or repair from outside. An iframe
//   pointed at a plain URL, opened by a plain React button, has no such
//   lifecycle to race.

import config from '../config.json';

// preAuthHubMappings only runs once, pre-auth, during the mobile login flow —
// it never re-fires on app reload/relaunch with a restored session. So the
// durable source of truth for a hub's chat config is config.json's
// hubSettings (keyed by hubCode), exactly like web already uses. We persist
// the resolved hubCode so a restored mobile session can look it up again
// without needing that API response to still be in memory.
export const ACTIVE_HUB_CODE_KEY = 'kwantu_active_mobile_hub_code';

/** Resolve a hub's chat config from config.json by hubCode (first match wins). */
export function getHubChatConfig(hubCode) {
  return config.hubSettings?.find((hub) => hub.hubCode === hubCode) || { chatEnabled: false };
}

let currentChatUUID = null;

const normalize = (chatUUID) => {
  if (!chatUUID) return null;
  return chatUUID.indexOf('/') === -1 ? `${chatUUID}/default` : chatUUID;
};

// Tawk's actual iframes use src="about:blank" or srcdoc, never a tawk.to
// URL — confirmed from the live DOM. A src-based selector never matches
// them, so destroy used to only ever remove the <script> tag and leave the
// entire widget (wrapper div + all iframes + a "chat-bubble" div) behind,
// uncleaned, for the next load to collide with. Identify Tawk's frames by
// their distinctive fixed 67x64px launcher size or very-high z-index
// instead, then remove their nearest ancestor that's a direct child of
// <body> — i.e. the whole wrapper Tawk appended — so nothing lingers.
const isTawkFrame = (el) => {
  if (el.tagName !== 'IFRAME') return false;
  if (el.getAttribute('width') === '67px' && el.getAttribute('height') === '64px') return true;
  const zIndex = parseInt(window.getComputedStyle(el).zIndex, 10);
  return zIndex >= 1000000; // Tawk sets z-index:1000003+ on all its frames
};

const removeTawkNodes = () => {
  document.querySelectorAll('script[src*="embed.tawk.to"]').forEach((el) => el.remove());

  const removed = new Set();
  document.querySelectorAll('iframe').forEach((frame) => {
    if (!isTawkFrame(frame)) return;
    let node = frame;
    while (node.parentElement && node.parentElement !== document.body) {
      node = node.parentElement;
    }
    if (!removed.has(node)) {
      removed.add(node);
      node.remove();
    }
  });
};

/** Hide + fully tear down the widget so a new property can be loaded cleanly. */
export function destroyTawk() {
  if (window.Tawk_API?.hideWidget) {
    window.Tawk_API.hideWidget();
  }
  removeTawkNodes();
  window.Tawk_API = undefined;
  window.Tawk_LoadStart = undefined;
  currentChatUUID = null;
}

/**
 * Load the Tawk.to widget for a given property/widget id ("propertyId" or
 * "propertyId/widgetId" — bare ids get "/default" appended, matching the
 * legacy app/scripts/config.js convention). No-op if that id is already loaded.
 */
export function loadTawk(chatUUID) {
  const target = normalize(chatUUID);
  if (!target) return;
  if (target === currentChatUUID && document.querySelector('script[src*="embed.tawk.to"]')) {
    return; // already showing this property
  }

  destroyTawk();

  window.Tawk_API = window.Tawk_API || {};
  window.Tawk_LoadStart = new Date();

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://embed.tawk.to/${target}`;
  script.charset = 'UTF-8';
  script.setAttribute('crossorigin', '*');
  document.getElementsByTagName('script')[0].parentNode.insertBefore(script, document.getElementsByTagName('script')[0]);

  currentChatUUID = target;
}

/**
 * Reconcile the widget against a { chatEnabled, chatUUID } pair — the shape
 * that both config.json hub entries and preAuthHubMappings results use.
 * Falls back to fallbackChatUUID (the global support widget) when a hub is
 * enabled but has no chatUUID of its own configured yet.
 */
export function syncTawk({ chatEnabled, chatUUID }, fallbackChatUUID) {
  if (chatEnabled === false) {
    destroyTawk();
    return;
  }
  const target = chatUUID || fallbackChatUUID;
  if (!target) {
    destroyTawk();
    return;
  }
  loadTawk(target);
}

export function getCurrentChatUUID() {
  return currentChatUUID;
}

/**
 * Tawk's direct chat-page URL — the mobile SupportChatModal loads this in an
 * iframe rather than injecting Tawk's embed script. Returns null when there's
 * no id to build a URL from (caller should treat that as chat-unavailable).
 */
export function getTawkChatUrl(chatUUID) {
  const target = normalize(chatUUID);
  return target ? `https://tawk.to/chat/${target}` : null;
}
